import { useQuery, useQueryClient } from "react-query";
import PrivateApi from "../../../services/PrivateApiService";
import { useAuthState, useAuthDispatch } from "../../../context/AuthContext";
import { useEffect, useState } from "react";
import ErrorMask from "./ErrorMask";
import LoadingMask from "./LoadingMask";
import { Button, Col, FloatingLabel, FormControl, Row, Spinner } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { FaUserCircle } from "react-icons/fa";
import Validator from "../../../services/Validator";
import EnergoTooltip from "../../shared/EnergoTooltip";

const EmployeeMask = ({formData, setFormData, formError, setFormError, nextHandler}) => {

    // ! -------------
    // ! Globar vars |
    // ! -------------
    const API = new PrivateApi();
    const VALIDATOR = new Validator();
    const { contactPerson } = useAuthState();
    const dispatch = useAuthDispatch();
    const queryClient = useQueryClient();

    // * Search and table vars
    const [fitleredEmployees, setFilteredEmployees] = useState([]);
    const [searchField, setSeachField] = useState("");
    const tableColumns = [
        { name: "Name", selector: row => row.name, sortable: true },
        { name: "E-Mail-Adresse", selector: row => row.email, sortable: true },
    ];
    const paginationOptions = {
        rowsPerPageText: 'Zeilen pro Seite',
        rangeSeparatorText: 'von',
        selectAllRowsItemText: 'Alle'
    };

    // * New Contact vars
    const [newContactShow, setNewContactShow] = useState(false);
    const [newContactSaveLoading, setNewContactSaveLoading] = useState(false);
    const [newContactData, setNewContactData] = useState({ name: "", email: "", phone: "" });
    const [fieldErrors, setFieldErrors] = useState({});


    // ! ---------------
    // ! Data fetching |
    // ! ---------------
    const { data, error, isLoading } = useQuery("GET_EMPLOYEES", API.get_Partner_employees, { staleTime: Infinity });


    // ! --------- 
    // ! Effects |
    // ! ---------
    // * Effect on data
    useEffect(() => {
        // if data exist and data isn't empty set it to filteredEmployees
        if (data && data.data.length > 0) {
            setFilteredEmployees(data.data);
        }

        // add background to selected row
        if (contactPerson && contactPerson !== null && contactPerson.id) {
            setFormData(prev => ({...prev, contact: contactPerson}));
            var elem = document.getElementById(`row-${formData.contactPerson}`);
            if (elem) {
                elem.classList.add('bg-energo');
            }
        }
    }, [data, contactPerson])

    // * Effect on search field change
    useEffect(() => {
        // if search field changed and it's not empty filter data
        if (data && data.data.length > 0 && searchField !== "") {
            return setFilteredEmployees(data.data.filter(e =>
                e.name.toLowerCase().includes(searchField.toLowerCase())
            ));
        }

        // if search field empty reset to all data
        if (data && data.data.length > 0) {
            setFilteredEmployees(data.data);
        }
    }, [searchField, data])


    // ! ------------
    // ! Functions  |
    // ! ------------
    // * Remove selected contactPerson
    const removeSelectedContactPerson = () => {
        setFormData(prev => ({...prev, contact: null}));
        dispatch("CLEAR_CONTACT");
    }


    // * Select employee
    const selectEmployee = (row) => {
        setFormError({}); // reset form errors

        // get selected rows till now
        const rows = document.querySelectorAll('[id*="row"]');
        const selectedRows = Array.from(rows).filter(elem => elem.classList.contains('bg-energo'));

        // if there was selected rows remove bachground and clear contact
        if (selectedRows.length > 0) {
            selectedRows.forEach(elem => {
                elem.classList.remove('bg-energo');
                setFormData(prev => ({...prev, contact: null}));
                dispatch('CLEAR_CONTACT');
            });
        }

        // find selected element
        var index = selectedRows.findIndex(elem => elem.id === `row-${row.id}`);
        // if found add background and select contact person
        if (index === -1) {
            var elem = document.getElementById(`row-${row.id}`);
            if (elem) {
                elem.classList.add('bg-energo');
                dispatch('POPULATE_CONTACT', row);
                setFormData(prev => ({...prev, contact: row}));
            }
        }
    };

    // * New contact cancel handler
    const newContactCancelHandler = () => {
        setNewContactData({ name: "", email: "", phone: "" });
        setNewContactShow(false);
    }

    // Save new contact
    const saveNewContact = () => {
        setNewContactSaveLoading(true); // set loading
        var tmpError = {}; // init temp errors

        // checks
        if (!newContactData.name || newContactData.name.length < 4) tmpError.name = true;
        if (!newContactData.email || !VALIDATOR.EmailValid(newContactData.email)) tmpError.email = true;

        // if tmpError isn't empty return errors
        if (Object.keys(tmpError).length > 0) {
            setNewContactSaveLoading(false);
            return setFieldErrors(tmpError);
        }

        // send data to API
        API.post_Partner_employee_save(newContactData)
            .then(res => {
                queryClient.invalidateQueries('GET_EMPLOYEES');
                setNewContactData({ name: "", email: "", phone: "" });
                setNewContactShow(false);
                dispatch('POPULATE_CONTACT', res.data);
                setFormData(prev => ({...prev, contact: res.data}))
            })
            .catch(err => {
                if (err.response.data.detail && err.response.data.detail !== "") {
                    setFieldErrors({apiError: err.response.data.detail})
                } else {
                    setFieldErrors({apiError: "Unbekanter Fehler aufgetreten."})
                }
            })
            .finally(() => {
                setNewContactSaveLoading(false);
            })
    }

    if (isLoading) return <LoadingMask />;
    if (error) return <ErrorMask error={error} />;

    return (
        <>
            {newContactShow === true && (contactPerson === undefined || contactPerson === null) &&
                <div className="mb-5 p-4" style={{backgroundColor: "darkgray", borderRadius: 15}}>
                    <p className="text-light"><b>Neuer Kontakt erstellen</b></p>
                    <Row>
                        <Col xs="12" md="8" xl="6">
                            <FloatingLabel label="Name und Nachname">
                                <FormControl
                                    placeholder="Name und Nachname"
                                    value={newContactData.name}
                                    onChange={e => {setNewContactData(prev => ({...prev, name: e.target.value})); setFieldErrors({})}}
                                    isValid={newContactData.name && newContactData.name.length > 3}
                                    isInvalid={fieldErrors.name}
                                />
                            </FloatingLabel>
                        </Col>
                    </Row>
                    <Row className="mt-3">
                        <Col xs="12" md="8" xl="6">
                            <FloatingLabel label="E-Mail-Adresse">
                                <FormControl
                                    placeholder="E-Mail-Adresse"
                                    value={newContactData.email}
                                    onChange={e => {setNewContactData(prev => ({...prev, email: e.target.value})); setFieldErrors({})}}
                                    isValid={VALIDATOR.EmailValid(newContactData.email)}
                                    isInvalid={fieldErrors.email}
                                />
                            </FloatingLabel>
                        </Col>
                        <Col xs="12" md="8" xl="6">
                            <FloatingLabel label="Telefonnummer">
                                <FormControl
                                    placeholder="Telefonnummer"
                                    value={newContactData.phone}
                                    onChange={e => {setNewContactData(prev => ({...prev, phone: e.target.value})); setFieldErrors({})}}
                                    isValid={newContactData.phone && newContactData.phone.length > 4}
                                />
                            </FloatingLabel>
                        </Col>
                    </Row>
                    <Row className="mt-3 text-end">
                        {fieldErrors.apiError &&
                            <Col xs="12">
                                <p className="text-danger">{fieldErrors.apiError}</p>
                            </Col>
                        }
                        <Col xs="12">
                            <Button onClick={newContactCancelHandler} className="me-3" variant="warning">Abbrechen</Button>
                            <Button 
                                onClick={saveNewContact}
                                disabled={newContactSaveLoading}
                                variant="energo"
                            >
                                {newContactSaveLoading?
                                    <Spinner animation="border" /> :
                                    "Speichern"
                                }
                            </Button>
                        </Col>
                    </Row>
                </div>
            }
            {contactPerson !== undefined && contactPerson !== null ?
                <div className="mb-5">
                    <div className="text-center mt-3">
                        <p className="text-muted">Ausgewählte Kontaktperson</p>
                        <FaUserCircle size={60} className="text-energo"/>
                        <p className="mt-3"><b>{contactPerson.name}</b></p>
                        <div className="mt-3">
                            <Button onClick={removeSelectedContactPerson} variant="warning" className="me-3">Das bin ich nicht</Button>
                            <Button onClick={() => nextHandler()} variant="energo">Ja, das bin ich</Button>
                        </div>
                    </div>
                </div>
            :   
                <>
                    <Row className="mt-3 align-items-center">
                        <Col xs="8">
                            <div style={{display: "flex"}}>
                                <b className="text-energo me-2">
                                    Bitte wählen Sie Ihre Kontaktinformation aus
                                </b>
                                <EnergoTooltip tKey="101" position="right" />
                            </div>
                        </Col>
                        <Col xs="4" className="text-end">
                            <Button onClick={() => setNewContactShow(true)} className="btn-sm btn-energo">Neuer Kontakt</Button> 
                        </Col>
                    </Row>
                    <Row className="mt-3">
                        <Col className="mb-1" xs="12" md="8" xl="4">
                            <FormControl
                                style={{borderRadius: 15}}
                                placeholder="Schnellsuche"
                                value={searchField}
                                onChange={e => setSeachField(e.target.value)}
                            />
                        </Col>
                        <Col xs="12">
                            <DataTable
                                data={fitleredEmployees}
                                columns={tableColumns}
                                noDataComponent="Keine Kontaktinformation gefunden"
                                onRowClicked={row => selectEmployee(row)}
                                paginationComponentOptions={paginationOptions}
                                pagination
                            />
                        </Col>
                    </Row>
                </>
            }
            {formError.contactPerson !== undefined &&
                <div className="text-center mt-3">
                    <p className="text-danger">Kontaktperson muss ausgewählt sein.</p>
                </div>
            }
        </>
    )
}
export default EmployeeMask;